import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Save, UserPlus2, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { runOrQueue } from "@/lib/offlineQueue";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { PlzCityInput, StreetAutocomplete } from "@/components/PlzCityInput";
import AutofillInput from "@/components/AutofillInput";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";

const BUILDING_TYPES = ["Einfamilienhaus","Doppelhaushälfte","Reihenhaus","Mehrfamilienhaus","Gewerbe","Sonstiges"];
const CUSTOMER_TYPES = ["Eigentümer","Mieter","Verwalter"];

const DRAFT_KEY = "novatus:project-new-draft:v1";
const EMPTY_FORM = {
  customer: {
    salutation: "",
    first_name: "", last_name: "", email: "", phone: "",
    street: "", house_number: "", zip: "", city: "",
    customer_type: "Eigentümer",
    // Iter88zbi — B2B-Flag für PV-USt-Klassifizierung. `null` = Auto-Detect
    // anhand der PV-Positions-MwSt; `true` = gewerblich (GmbH/AG); `false` =
    // explizit Privatperson. Kann später am Kundendatensatz geändert werden.
    is_business: false,
    // Iter88zbj — B2B-Rechnungsdaten (Firmenname & USt-ID). Werden im
    // PV-Angebots-PDF im Adressblock ausgegeben, sobald is_business=true.
    company_name: "",
    vat_id: "",
  },
  building_type: "Einfamilienhaus",
  desired_installation_date: "",
  source: "",
  notes: "",
};

const isDraftMeaningful = (f) => {
  if (!f || !f.customer) return false;
  const c = f.customer;
  return !!(c.first_name || c.last_name || c.email || c.phone || c.street || c.house_number || c.zip || c.city || f.notes || f.source || f.desired_installation_date);
};

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">
      {label}{required && <span className="text-[#00E575]"> *</span>}
    </label>
    {children}
  </div>
);

const inputCls = "h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none";

export default function ProjectNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [restoredNotice, setRestoredNotice] = useState(false);
  const hydratedRef = useRef(false);

  // Restore draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isDraftMeaningful(parsed)) {
          setForm({ ...EMPTY_FORM, ...parsed, customer: { ...EMPTY_FORM.customer, ...(parsed.customer || {}) } });
          setRestoredNotice(true);
        }
      }
    } catch { /* ignore corrupt draft */ }
    hydratedRef.current = true;
  }, []);

  // Persist form to localStorage on every change (but not before hydration)
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      if (isDraftMeaningful(form)) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch { /* quota exceeded etc. — ignore */ }
  }, [form]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
    setForm(EMPTY_FORM);
    setRestoredNotice(false);
    toast.success("Entwurf verworfen");
  };

  // Iter89y (2026-02-25) — Funktionales setState statt Spread über Closure.
  // Verhindert Stale-Closure beim schnellen Hintereinander-Ändern mehrerer
  // Felder (z.B. Anrede wählen → sofort Vorname tippen) — bisher kam es
  // vor, dass der zweite setForm den eben gesetzten Wert des ersten
  // überschrieb und die Anrede auf „leer" zurücksprang.
  const updCust = (k, v) =>
    setForm((prev) => ({ ...prev, customer: { ...prev.customer, [k]: v } }));
  const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    const c = form.customer;
    if (!c.first_name || !c.last_name || !c.street || !c.house_number || !c.zip || !c.city) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    setLoading(true);
    try {
      const r = await runOrQueue({
        method: "post",
        url: "/projects",
        data: form,
        label: `Projekt anlegen: ${c.last_name}, ${c.first_name}`,
      });
      // Only clear draft on real success (or queued offline) — never on 403/500
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
      if (r.offline) {
        toast.success("Offline – Projekt wird synchronisiert, sobald online");
        navigate("/projects");
      } else {
        toast.success("Projekt angelegt");
        // Explizit ?tab=customer setzen, damit das neu angelegte Projekt IMMER
        // auf dem Kunde-Tab öffnet (nicht auf einem gespeicherten letzten Tab).
        navigate(`/projects/${r.data.id}?tab=customer`);
      }
    } catch (e) {
      // Draft bleibt bewusst erhalten — der User kann's nach einem Server-
      // Fehler (z. B. 403, Netzwerk-Timeout) einfach nochmal versuchen.
      toast.error(
        e.response?.data?.detail || "Fehler beim Anlegen",
        { description: "Deine Eingaben bleiben gespeichert und können später fortgesetzt werden." }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Neues Projekt" back="/dashboard" />
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center">
            <UserPlus2 className="text-[#00E575]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Kundendaten erfassen</h1>
            <p className="text-sm text-zinc-300 font-medium">Schritt 1 von 6 – Kunden- und Projektgrundlagen</p>
          </div>
        </div>

        {restoredNotice && (
          <div
            data-testid="newproject-draft-restored-banner"
            className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3"
          >
            <RotateCcw className="mt-0.5 shrink-0 text-amber-300" size={18} />
            <div className="flex-1 text-sm leading-snug">
              <div className="font-semibold text-amber-200">Entwurf wiederhergestellt</div>
              <div className="text-zinc-300">
                Deine zuletzt eingegebenen Daten wurden automatisch geladen. Du kannst direkt fortsetzen oder verwerfen.
              </div>
            </div>
            <button
              type="button"
              data-testid="newproject-draft-discard-btn"
              onClick={clearDraft}
              className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-amber-500/40 bg-transparent px-2.5 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20 transition"
              title="Entwurf verwerfen und Formular leeren"
            >
              <X size={12} /> Verwerfen
            </button>
          </div>
        )}

        <div className="space-y-6">
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
            <h2 className="font-display text-lg font-semibold">Kunde</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Anrede">
                <select
                  data-testid="newproject-salutation"
                  className={inputCls}
                  value={form.customer.salutation}
                  onChange={(e) => updCust("salutation", e.target.value)}
                >
                  <option value="">(keine Anrede)</option>
                  <option value="male">Herr</option>
                  <option value="female">Frau</option>
                  <option value="family">Familie</option>
                  <option value="diverse">Divers</option>
                </select>
              </Field>
              <div /> {/* spacer to keep the 2-column grid balanced */}
              <Field label="Vorname" required>
                <input data-testid="newproject-firstname" className={inputCls} value={form.customer.first_name} onChange={(e) => updCust("first_name", e.target.value)} />
              </Field>
              <Field label="Nachname" required>
                <input data-testid="newproject-lastname" className={inputCls} value={form.customer.last_name} onChange={(e) => updCust("last_name", e.target.value)} />
              </Field>
              <Field label="Telefon">
                <AutofillInput
                  dataTestid="newproject-phone"
                  className={inputCls}
                  storageKey="customer_phone"
                  type="tel"
                  autoComplete="tel"
                  value={form.customer.phone}
                  onChange={(v) => updCust("phone", v)}
                />
              </Field>
              <Field label="E-Mail">
                <AutofillInput
                  dataTestid="newproject-email"
                  className={inputCls}
                  storageKey="customer_email"
                  type="email"
                  autoComplete="email"
                  value={form.customer.email}
                  onChange={(v) => updCust("email", v)}
                />
              </Field>
            </div>
          </section>

          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
            <h2 className="font-display text-lg font-semibold">Adresse</h2>
            {/* Iter97 — PLZ+Ort MUSS vor Straße stehen, damit die Auto-Fill-Kette
                (PLZ → Ort auto → Straße-Autocomplete via openplzapi) funktioniert. */}
            <div className="grid grid-cols-3 gap-3">
              <PlzCityInput
                zip={form.customer.zip}
                city={form.customer.city}
                onZipChange={(v) => updCust("zip", v)}
                onCityChange={(v) => updCust("city", v)}
                zipTestId="newproject-zip"
                cityTestId="newproject-city"
              />
              <div className="col-span-2">
                <StreetAutocomplete
                  zip={form.customer.zip}
                  street={form.customer.street}
                  onStreetChange={(v) => updCust("street", v)}
                  testid="newproject-street"
                />
              </div>
              <Field label="Nr." required>
                <input data-testid="newproject-housenumber" className={inputCls} value={form.customer.house_number} onChange={(e) => updCust("house_number", e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
            <h2 className="font-display text-lg font-semibold">Objekt</h2>
            <Field label="Gebäudetyp">
              <select data-testid="newproject-buildingtype" className={inputCls} value={form.building_type} onChange={(e) => upd("building_type", e.target.value)}>
                {BUILDING_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Eigentümerstatus">
              <select data-testid="newproject-customertype" className={inputCls} value={form.customer.customer_type} onChange={(e) => updCust("customer_type", e.target.value)}>
                {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            {/* Iter88zbi — B2B/B2C-Klassifizierung für PV-USt */}
            <Field label="Kundentyp (Umsatzsteuer)">
              <select data-testid="newproject-isbusiness" className={inputCls}
                value={form.customer.is_business ? "business" : "private"}
                onChange={(e) => updCust("is_business", e.target.value === "business")}>
                <option value="private">Privatperson (0 % USt · § 12 Abs. 3 UStG)</option>
                <option value="business">Gewerblich (19 % Mehrwertsteuer)</option>
              </select>
            </Field>
            {/* Iter88zbj — B2B-Rechnungsdaten (nur wenn gewerblich) */}
            {form.customer.is_business && (
              <>
                <Field label="Firmenname" required>
                  <input
                    data-testid="newproject-companyname"
                    className={inputCls}
                    placeholder="z. B. Müller GmbH"
                    value={form.customer.company_name || ""}
                    onChange={(e) => updCust("company_name", e.target.value)}
                  />
                </Field>
                <Field label="USt-ID (Umsatzsteuer-Identifikationsnummer)">
                  <input
                    data-testid="newproject-vatid"
                    className={inputCls}
                    placeholder="z. B. DE123456789"
                    value={form.customer.vat_id || ""}
                    onChange={(e) => updCust("vat_id", e.target.value.toUpperCase())}
                    autoCapitalize="characters"
                  />
                </Field>
              </>
            )}
            <Field label="Gewünschter Umsetzungstermin">
              <input data-testid="newproject-desireddate" className={inputCls} type="date" value={form.desired_installation_date || ""} onChange={(e) => upd("desired_installation_date", e.target.value)} />
            </Field>
            <Field label="Lead-Quelle">
              <input data-testid="newproject-source" className={inputCls} placeholder="z. B. Empfehlung, Messe, Website" value={form.source} onChange={(e) => upd("source", e.target.value)} />
            </Field>
            <Field label="Bemerkungen">
              <DictaphoneTextarea
                value={form.notes}
                onChange={(v) => upd("notes", v)}
                testid="newproject-notes"
                rows={3}
                placeholder="Bemerkungen zum Projekt (oder Mic-Button für Diktat)"
              />
            </Field>
          </section>
        </div>
      </main>

      {/* Fixed save bar */}
      <div data-bottom-bar className="fixed bottom-0 left-0 right-0 bg-[#09090B]/95 backdrop-blur-xl border-t border-zinc-800 p-4 z-30">
        <div className="max-w-3xl mx-auto">
          <Button
            data-testid="newproject-save-btn"
            onClick={submit}
            disabled={loading}
            className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition"
          >
            {loading ? <Loader2 className="animate-spin" /> : <><Save size={20} className="mr-2" /> Projekt anlegen</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
